import { TimezoneStore } from './timezone-store';

const _getTimezone = () => TimezoneStore.getTimezone();

/*
 * Stores Intl.DateTimeFormat objects by timezone.
 * Intl.DateTimeFormat construction is expensive — it loads ICU locale data
 * and resolves timezone rules on every instantiation.
 * Two are stored per timezone:
 *   shortOffset — gets the UTC offset (e.g. 'GMT+5:30', 'GMT-4')
 *   long        — gets the timezone name (e.g. 'India Standard Time')
 */
const currentDtf: Record<string, { shortOffset: Intl.DateTimeFormat; long: Intl.DateTimeFormat; }> = {};
let lastTz = '';

/*
 * Returns the cached Intl.DateTimeFormat pair for the given timezone.
 * If the timezone has changed, the previous timezone is cleared
 * and new tz is added.
 */
function getDtf(tz: string) {
  if (tz !== lastTz) {
    // Timezone changed — wipe the old date time format entry
    for (const key in currentDtf) delete currentDtf[key];
    lastTz = tz;
  }
 
  if (!currentDtf[tz]) {
    currentDtf[tz] = {
      shortOffset: new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }),
      long: new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' }),
    };
  }
  return currentDtf[tz];
}


(function () {
  const _OriginalDate = Date;

  /*
   * Returns the UTC offset in milliseconds for the current timezone
   * at the given UTC timestamp. Uses the cached Intl.DateTimeFormat to
   * extract the GMT offset string and parses it into milliseconds.
   * Handles all Intl offset formats: 'GMT+5:30', 'GMT-4', 'GMT-11', 'GMT'.
   */
  function getOffsetMs(utcMs: number): number {
    const tz = _getTimezone();
    const parts = getDtf(tz).shortOffset.formatToParts(new _OriginalDate(utcMs));
    const tzPart = parts.find(p => p.type === 'timeZoneName')!.value;
    
    const match = tzPart.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    if (!match) return 0;

    const sign = match[1] === '-' ? -1 : 1;
    const h = parseInt(match[2], 10);
    const m = match[3] ? parseInt(match[3], 10) : 0;
    return sign * (h * 60 + m) * 60 * 1000;
  }

  /*
   * Converts a date/time expressed in the browser's timezone to a UTC timestamp.
   *
   * steps:
   *   1. Treat the browser-timezone args as UTC temporarily to get a raw timestamp
   *   2. Get the browser's timezone offset at that timestamp
   *   3. Subtract the offset to land on the correct UTC time
   *
   * Example: 10:00 IST (UTC+5:30) => 10:00 - 5:30 = 04:30 UTC
   */
  function tzToUtcMs(year: number, month: number, day: number,
    hours: number, minutes: number, seconds: number, ms: number): number {
    const browserTzAsUtc = _OriginalDate.UTC(year, month, day, hours, minutes, seconds, ms);
    return browserTzAsUtc - getOffsetMs(browserTzAsUtc);
  }

  class CustomDate extends _OriginalDate {

    /*
     * Overrides the native Date constructor to interpret multi-arg calls
     * (year, month, day, hours ...) as time in the browser timezone
     * rather than the system timezone.
     *
     * Single-arg forms (timestamp, ISO string) and no-arg form pass straight
     * through to the native constructor — they carry no timezone ambiguity.
     *
     * Multi-arg form: args are validated, then passed to tzToUtcMs which
     * computes the correct UTC timestamp for the browser'scurrent timezone and
     * stores it via super(corrected).
     */
    constructor(...args: ConstructorParameters<typeof Date>) {
      if (args.length >= 2) {
        if (args.some(arg => typeof arg !== 'number' || isNaN(arg))) {
          super(NaN);
          return;
        }

        const [year, month, date = 1, hours = 0, minutes = 0, seconds = 0, ms = 0] = args as number[];
        const corrected = tzToUtcMs(year, month, date, hours, minutes, seconds, ms);
        super(corrected);
      } else {
        super(...args);
      }
    }

    /*
     * Returns date/time fields for the current timezone.
     *
     * Shifts the stored UTC timestamp forward by the timezone offset,
     * then reads back using getUTC* — which reads raw numbers with no
     * system timezone interference.
     *
     * Example: 04:30 UTC + IST offset(+5:30) = 10:00 => getUTCHours() = 10
     */
    private getDateParts(): Record<string, any> {
      const utcMs = this.getTime();
      const offsetMs = getOffsetMs(utcMs);
      const d = new _OriginalDate(utcMs + offsetMs);
      const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

      return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth(),
        day: d.getUTCDate(),
        weekday: WEEKDAY[d.getUTCDay()],
        hour: d.getUTCHours(),
        minute: d.getUTCMinutes(),
        second: d.getUTCSeconds(),
      };
    }
    
    /*
     * Returns the GMT offset for the current timezone in a fixed format.
     * Always returns "GMT±HHMM".
     *
     * Examples:
     *   "GMT"      -> "GMT+0000"
     *   "GMT+5:30" -> "GMT+0530"
     *   "GMT-4"    -> "GMT-0400"
     */
    private formatGmtOffset(): string {
      const offsetParts = getDtf(_getTimezone()).shortOffset.formatToParts(new _OriginalDate(this.getTime()));
      let gmt = offsetParts.find((p) => p.type === "timeZoneName")!.value;
      return gmt === "GMT" ? "GMT+0000" : gmt.replace(
        /GMT([+-]\d{1,2})(?::?(\d{2}))?/,
        (_, hours, minutes = "00") => {
          const sign = hours[0];
          const hh = hours.slice(1).padStart(2, "0");
          return `GMT${sign}${hh}${minutes}`;
        }
      );
    }

    /*
     * Returns the long-form timezone name for the current timezone
     * at the current timestamp (e.g. 'India Standard Time', 'Eastern Daylight Time').
     */
    private formatLongName(): string {
      const tz = _getTimezone();
      return getDtf(tz).long
        .formatToParts(new _OriginalDate(this.getTime()))
        .find((p) => p.type === "timeZoneName")!.value;
    }

    /*
     * Returns the full date string in the same format as native Date.toString()
     * respective to the current timezone instead of the system timezone.
     * Format: "Sat Jun 15 2024 10:00:00 GMT+0530 (India Standard Time)"
     */
    private dateFormat(): string {
      const map = this.getDateParts();
      const pad = (n: number) => String(n).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

      return (
        `${map["weekday"]} ${months[Number(map["month"])]} ${pad(map["day"])} ${map["year"]} ` +
        `${pad(map["hour"])}:${pad(map["minute"])}:${pad(map["second"])} ${this.formatGmtOffset()} (${this.formatLongName()})`
      );
    }

    toString(): string { return this.dateFormat(); }
    
    toDateString(): string {
      const map = this.getDateParts();
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${map["weekday"]} ${months[Number(map["month"])]} ${String(map["day"]).padStart(2, "0")} ${map["year"]}`;
    }

    toTimeString(): string {
      const map = this.getDateParts();
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${pad(map["hour"])}:${pad(map["minute"])}:${pad(map["second"])} ${this.formatGmtOffset()} (${this.formatLongName()})`;
    }

    toISOString(): string { return new _OriginalDate(this.getTime()).toISOString(); }

    toLocaleString(): string;
    toLocaleString(locale?: string | string[], opts?: Intl.DateTimeFormatOptions): string;
    toLocaleString(locale?: string | string[], opts: Intl.DateTimeFormatOptions = {}): string {
      opts.timeZone = opts.timeZone ?? _getTimezone();
      return new _OriginalDate(this.getTime()).toLocaleString(locale, opts);
    }

    toLocaleDateString(): string;
    toLocaleDateString(locale?: string | string[], opts?: Intl.DateTimeFormatOptions): string;
    toLocaleDateString(locale?: string | string[], opts: Intl.DateTimeFormatOptions = {}): string {
      opts.timeZone = opts.timeZone ?? _getTimezone();
      return new _OriginalDate(this.getTime()).toLocaleDateString(locale, opts);
    }

    toLocaleTimeString(): string;
    toLocaleTimeString(locale?: string | string[], opts?: Intl.DateTimeFormatOptions): string;
    toLocaleTimeString(locale?: string | string[], opts: Intl.DateTimeFormatOptions = {}): string {
      opts.timeZone = opts.timeZone ?? _getTimezone();
      return new _OriginalDate(this.getTime()).toLocaleTimeString(locale, opts);
    }

    valueOf(): number { return super.getTime(); }

    getTime(): number { return super.getTime(); }
    getFullYear(): number { return this.getDateParts()["year"]; }
    getMonth(): number { return this.getDateParts()["month"]; }
    getDate(): number { return this.getDateParts()["day"]; }
    getDay(): number {
      const WEEKDAY_MAP: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return WEEKDAY_MAP[this.getDateParts()["weekday"]];
    }
    getHours(): number { return this.getDateParts()["hour"]; }
    getMinutes(): number { return this.getDateParts()["minute"]; }
    getSeconds(): number { return this.getDateParts()["second"]; }
    getMilliseconds(): number { return this.getTime() % 1000; }
    getTimezoneOffset(): number {
      // getTimezoneOffset returns the difference in minutes between UTC and local time
      // Positive values mean the browser timezone is behind UTC (e.g., EST is UTC-5 = +300)
      // Negative values mean the browser timezone is ahead of UTC (e.g., IST is UTC+5:30 = -330)
      const offsetMs = getOffsetMs(this.getTime());
      return -Math.round(offsetMs / (60 * 1000));
    }

    getUTCFullYear(): number { return super.getUTCFullYear(); }
    getUTCMonth(): number { return super.getUTCMonth(); }
    getUTCDate(): number { return super.getUTCDate(); }
    getUTCDay(): number { return super.getUTCDay(); }
    getUTCHours(): number { return super.getUTCHours(); }
    getUTCMinutes(): number { return super.getUTCMinutes(); }
    getUTCSeconds(): number { return super.getUTCSeconds(); }
    getUTCMilliseconds(): number { return super.getUTCMilliseconds(); }

    toUTCString(): string {
      return super.toUTCString();
    }

    toJSON(): string {
      return this.toISOString();
    }

    private _setFromLocal(year: number, month: number, day: number,
      hours: number, minutes: number, seconds: number, ms: number): number {
      const finalTs = tzToUtcMs(year, month, day, hours, minutes, seconds, ms);
      super.setTime(finalTs);
      return finalTs;
    }

    setTime(timeValue: number): number {
      return super.setTime(timeValue);
    }

    setMilliseconds(ms: number): number {
      const p = this.getDateParts();
      return this._setFromLocal(p.year, p.month, p.day, p.hour, p.minute, p.second, ms);
    }

    setSeconds(sec: number, ms: number = this.getMilliseconds()): number {
      const p = this.getDateParts();
      return this._setFromLocal(p.year, p.month, p.day, p.hour, p.minute, sec, ms);
    }

    setMinutes(min: number, sec: number = this.getSeconds(), ms: number = this.getMilliseconds()): number {
      const p = this.getDateParts();
      return this._setFromLocal(p.year, p.month, p.day, p.hour, min, sec, ms);
    }

    setHours(hours: number, min: number = this.getMinutes(), sec: number = this.getSeconds(), ms: number = this.getMilliseconds()): number {
      const p = this.getDateParts();
      return this._setFromLocal(p.year, p.month, p.day, hours, min, sec, ms);
    }

    setDate(day: number): number {
      const p = this.getDateParts();
      return this._setFromLocal(p.year, p.month, day, p.hour, p.minute, p.second, this.getMilliseconds());
    }

    setMonth(month: number, day: number = this.getDate()): number {
      const p = this.getDateParts();
      return this._setFromLocal(p.year, month, day, p.hour, p.minute, p.second, this.getMilliseconds());
    }

    setFullYear(year: number, month: number = this.getMonth(), day: number = this.getDate()): number {
      const p = this.getDateParts();
      return this._setFromLocal(year, month, day, p.hour, p.minute, p.second, this.getMilliseconds());
    }

    setUTCMilliseconds(ms: number): number { return super.setUTCMilliseconds(ms); }

    setUTCSeconds(sec: number, ms?: number): number {
      return ms !== undefined ? super.setUTCSeconds(sec, ms) : super.setUTCSeconds(sec);
    }

    setUTCMinutes(min: number, sec?: number, ms?: number): number {
      if (ms !== undefined) return super.setUTCMinutes(min, sec!, ms);
      if (sec !== undefined) return super.setUTCMinutes(min, sec);
      return super.setUTCMinutes(min);
    }

    setUTCHours(hours: number, min?: number, sec?: number, ms?: number): number {
      if (ms !== undefined) return super.setUTCHours(hours, min!, sec!, ms);
      if (sec !== undefined) return super.setUTCHours(hours, min!, sec);
      if (min !== undefined) return super.setUTCHours(hours, min);
      return super.setUTCHours(hours);
    }

    setUTCDate(day: number): number { return super.setUTCDate(day); }

    setUTCMonth(month: number, day?: number): number {
      return day !== undefined ? super.setUTCMonth(month, day) : super.setUTCMonth(month);
    }

    setUTCFullYear(year: number, month?: number, day?: number): number {
      if (day !== undefined) return super.setUTCFullYear(year, month!, day);
      if (month !== undefined) return super.setUTCFullYear(year, month);
      return super.setUTCFullYear(year);
    }

    static now(): number { return _OriginalDate.now(); }
    static parse(s: string): number { return _OriginalDate.parse(s); }
    static UTC(...args: Parameters<typeof Date.UTC>): number { return _OriginalDate.UTC(...args); }
  }

  try {
    (globalThis as any).Date = CustomDate;
  } catch (e) {
    console.error('Date override failed, using native Date', e);
  }
})();